import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getAuditLogs, getAuditLogStats } from "@/data";
import { AdminLayout } from "../shared/layout";
import { DataTable } from "@/components/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Activity,
  Filter,
  Search,
  Eye,
  Calendar,
  User,
  Smartphone,
  Monitor,
} from "lucide-react";
import { format } from "date-fns";

export function AuditLogsScreen() {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    userId: "",
    action: "",
    entityType: "",
    platform: "",
    startDate: "",
    endDate: "",
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Audit logs query
  const { data: logsData, isLoading } = useQuery({
    queryKey: ["audit-logs", page, filters],
    queryFn: () =>
      getAuditLogs({
        page,
        limit: 20,
        ...filters,
      }),
  });

  // Stats query
  const { data: statsData } = useQuery({
    queryKey: ["audit-log-stats"],
    queryFn: () => getAuditLogStats({ days: 30 }),
  });

  const logs = logsData?.data?.logs || [];
  const pagination = logsData?.data?.pagination || {};
  const stats = statsData?.data || {};

  // Action badge renkleri
  const getActionBadgeVariant = (action) => {
    if (action.includes("CREATE")) return "default";
    if (action.includes("UPDATE")) return "secondary";
    if (action.includes("DELETE")) return "destructive";
    return "outline";
  };

  // Platform icon
  const getPlatformIcon = (platform) => {
    if (platform === "mobile") return <Smartphone className="h-4 w-4" />;
    if (platform === "web") return <Monitor className="h-4 w-4" />;
    return <Activity className="h-4 w-4" />;
  };

  // Filtreleri temizle
  const clearFilters = () => {
    setFilters({
      userId: "",
      action: "",
      entityType: "",
      platform: "",
      startDate: "",
      endDate: "",
    });
    setPage(1);
  };

  // Tablo kolonları
  const columns = [
    {
      key: "createdAt",
      label: "Date/Time",
      render: (row) => (
        <div className="text-sm">
          <div className="font-medium">
            {format(new Date(row.createdAt), "MMM dd, yyyy")}
          </div>
          <div className="text-muted-foreground">
            {format(new Date(row.createdAt), "HH:mm:ss")}
          </div>
        </div>
      ),
    },
    {
      key: "user",
      label: "User",
      render: (row) => (
        <div className="text-sm">
          <div className="font-medium">{row.user?.name || "System"}</div>
          <div className="text-muted-foreground">{row.user?.email || "-"}</div>
        </div>
      ),
    },
    {
      key: "action",
      label: "Action",
      render: (row) => (
        <Badge variant={getActionBadgeVariant(row.action)}>{row.action}</Badge>
      ),
    },
    {
      key: "entityType",
      label: "Entity",
      render: (row) => (
        <div className="text-sm">
          <div className="font-medium">{row.entityType}</div>
          {row.entityId && (
            <div className="text-muted-foreground">ID: {row.entityId}</div>
          )}
        </div>
      ),
    },
    {
      key: "platform",
      label: "Platform",
      render: (row) => (
        <div className="flex items-center space-x-2">
          {getPlatformIcon(row.platform)}
          <span className="text-sm capitalize">{row.platform || "unknown"}</span>
        </div>
      ),
    },
    {
      key: "ipAddress",
      label: "IP Address",
      render: (row) => (
        <span className="text-sm font-mono">{row.ipAddress || "-"}</span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) => (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSelectedLog(row)}
        >
          <Eye className="h-4 w-4" />
        </Button>
      ),
    },
  ];

  return (
    <AdminLayout
      title="Audit Logs"
      description="Track all system activities and changes"
    >
      <div className="space-y-6">
        {/* Statistics Cards */}
        {stats.totalLogs && (
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Total Logs
                </CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totalLogs}</div>
                <p className="text-xs text-muted-foreground">Last 30 days</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Most Common Action
                </CardTitle>
                <Filter className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {stats.actionDistribution?.[0]?.action || "-"}
                </div>
                <p className="text-xs text-muted-foreground">
                  {stats.actionDistribution?.[0]?.count || 0} occurrences
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Web Platform
                </CardTitle>
                <Monitor className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {stats.platformDistribution?.find(
                    (p) => p.platform === "web"
                  )?.count || 0}
                </div>
                <p className="text-xs text-muted-foreground">Activities</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Mobile Platform
                </CardTitle>
                <Smartphone className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {stats.platformDistribution?.find(
                    (p) => p.platform === "mobile"
                  )?.count || 0}
                </div>
                <p className="text-xs text-muted-foreground">Activities</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Filters</CardTitle>
              <div className="space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <Filter className="h-4 w-4 mr-2" />
                  {showFilters ? "Hide" : "Show"} Filters
                </Button>
                {Object.values(filters).some((v) => v !== "") && (
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    Clear All
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>

          {showFilters && (
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="action">Action</Label>
                  <Select
                    value={filters.action || "all"}
                    onValueChange={(value) =>
                      setFilters({ ...filters, action: value === "all" ? "" : value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All actions" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All actions</SelectItem>
                      <SelectItem value="USER_CREATE">USER_CREATE</SelectItem>
                      <SelectItem value="USER_UPDATE">USER_UPDATE</SelectItem>
                      <SelectItem value="USER_DELETE">USER_DELETE</SelectItem>
                      <SelectItem value="SUBSCRIPTION_CREATE">
                        SUBSCRIPTION_CREATE
                      </SelectItem>
                      <SelectItem value="SUBSCRIPTION_UPDATE">
                        SUBSCRIPTION_UPDATE
                      </SelectItem>
                      <SelectItem value="SUBSCRIPTION_CANCEL">
                        SUBSCRIPTION_CANCEL
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="entityType">Entity Type</Label>
                  <Select
                    value={filters.entityType || "all"}
                    onValueChange={(value) =>
                      setFilters({ ...filters, entityType: value === "all" ? "" : value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All types</SelectItem>
                      <SelectItem value="USER">USER</SelectItem>
                      <SelectItem value="SUBSCRIPTION">SUBSCRIPTION</SelectItem>
                      <SelectItem value="PLAN">PLAN</SelectItem>
                      <SelectItem value="ROLE">ROLE</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="platform">Platform</Label>
                  <Select
                    value={filters.platform || "all"}
                    onValueChange={(value) =>
                      setFilters({ ...filters, platform: value === "all" ? "" : value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All platforms" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All platforms</SelectItem>
                      <SelectItem value="web">Web</SelectItem>
                      <SelectItem value="mobile">Mobile</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="startDate">Start Date</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={filters.startDate}
                    onChange={(e) =>
                      setFilters({ ...filters, startDate: e.target.value })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="endDate">End Date</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={filters.endDate}
                    onChange={(e) =>
                      setFilters({ ...filters, endDate: e.target.value })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="userId">User ID</Label>
                  <Input
                    id="userId"
                    type="number"
                    placeholder="Enter user ID"
                    value={filters.userId}
                    onChange={(e) =>
                      setFilters({ ...filters, userId: e.target.value })
                    }
                  />
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Logs Table */}
        <DataTable
          data={logs}
          columns={columns}
          title={`Audit Logs (${pagination.total || 0} total)`}
          enableSearch={false}
          enableSort={false}
          enablePagination={true}
          itemsPerPage={20}
          currentPage={page}
          totalPages={pagination.totalPages || 1}
          onPageChange={setPage}
          loading={isLoading}
        />
      </div>

      {/* Detail Dialog */}
      {selectedLog && (
        <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Audit Log Details</DialogTitle>
              <DialogDescription>
                {format(new Date(selectedLog.createdAt), "PPpp")}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">User</Label>
                  <div className="font-medium">
                    {selectedLog.user?.name || "System"}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {selectedLog.user?.email || "-"}
                  </div>
                </div>

                <div>
                  <Label className="text-muted-foreground">Action</Label>
                  <div>
                    <Badge variant={getActionBadgeVariant(selectedLog.action)}>
                      {selectedLog.action}
                    </Badge>
                  </div>
                </div>

                <div>
                  <Label className="text-muted-foreground">Entity Type</Label>
                  <div className="font-medium">{selectedLog.entityType}</div>
                </div>

                <div>
                  <Label className="text-muted-foreground">Entity ID</Label>
                  <div className="font-medium">
                    {selectedLog.entityId || "-"}
                  </div>
                </div>

                <div>
                  <Label className="text-muted-foreground">Platform</Label>
                  <div className="flex items-center space-x-2">
                    {getPlatformIcon(selectedLog.platform)}
                    <span className="capitalize">
                      {selectedLog.platform || "unknown"}
                    </span>
                  </div>
                </div>

                <div>
                  <Label className="text-muted-foreground">IP Address</Label>
                  <div className="font-mono text-sm">
                    {selectedLog.ipAddress || "-"}
                  </div>
                </div>
              </div>

              {selectedLog.userAgent && (
                <div>
                  <Label className="text-muted-foreground">User Agent</Label>
                  <div className="text-sm mt-1 p-2 bg-muted rounded">
                    {selectedLog.userAgent}
                  </div>
                </div>
              )}

              {selectedLog.metadata && (
                <div>
                  <Label className="text-muted-foreground">Metadata</Label>
                  <pre className="text-sm mt-1 p-2 bg-muted rounded overflow-auto max-h-64">
                    {JSON.stringify(JSON.parse(selectedLog.metadata), null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </AdminLayout>
  );
}

